import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loading, NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL, fmtDateTime } from "@/lib/format";

const BLANK = { username: "", name: "", email: "", password: "", role: "operator", tenant_id: "", active: true };
const PLATFORM = ["superadmin", "supervisor", "collector"];

function UserDialog({ open, onOpenChange, initial, roles, tenants, onSave }) {
  const [f, setF] = useState(BLANK);
  useEffect(() => {
    if (open) setF(initial ? { ...BLANK, ...initial, password: "", role: initial.platform_role || initial.tenant_role, tenant_id: initial.tenant_id || "" } : { ...BLANK, role: roles.some((r) => r.key === "operator") ? "operator" : roles[0].key });
  }, [open, initial, roles]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{initial ? "Ubah Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3" data-testid="user-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Username</Label><Input value={f.username} onChange={set("username")} required data-testid="user-username-input" /></div>
            <div><Label>Nama</Label><Input value={f.name} onChange={set("name")} required data-testid="user-name-input" /></div>
          </div>
          <div><Label>Email (opsional)</Label><Input value={f.email || ""} onChange={set("email")} data-testid="user-email-input" /></div>
          <div><Label>{initial ? "Password baru (kosongkan jika tidak diubah)" : "Password (min. 6)"}</Label><Input type="password" value={f.password} onChange={set("password")} data-testid="user-password-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Level Akses</Label>
              <NativeSelect value={f.role} onChange={set("role")} className="w-full" data-testid="user-role-select">
                {initial && !roles.some((r) => r.key === f.role) && f.role && <option value={f.role}>{initial.role_label || f.role} (kustom)</option>}
                {roles.map((r) => <option key={r.key} value={r.key}>{r.label}{r.custom ? " (kustom)" : ""}</option>)}
              </NativeSelect>
            </div>
            {tenants && !PLATFORM.includes(f.role) && (
              <div><Label>Perusahaan</Label>
                <NativeSelect value={f.tenant_id} onChange={set("tenant_id")} className="w-full" data-testid="user-tenant-select">
                  <option value="">— pilih —</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </NativeSelect>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF((s) => ({ ...s, active: v }))} data-testid="user-active-switch" /><span className="text-sm">Akun aktif</span></div>
          <DialogFooter><Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="user-form-submit">Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function UsersManager({ apiBase, roles: roleList, tenants, lockedRoles = [], filterTenant = "", extra }) {
  const roles = roleList.map((r) => (typeof r === "string" ? { key: r, label: ROLE_LABEL[r] } : r));
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(apiBase, { params: filterTenant ? { tenant_id: filterTenant } : {} });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [apiBase, filterTenant]);
  useEffect(() => {
    load();
  }, [load]);
  const save = async (f) => {
    const body = { username: f.username, name: f.name, email: f.email, password: f.password || null, tenant_role: f.role, active: f.active };
    if (tenants) body.tenant_id = PLATFORM.includes(f.role) ? null : f.tenant_id || null;
    try {
      if (dlg.initial) await api.put(`${apiBase}/${dlg.initial.id}`, body);
      else await api.post(apiBase, body);
      toast.success("Pengguna disimpan");
      setDlg({ open: false, initial: null });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return <Loading />;
  return (
    <div className="space-y-3" data-testid="users-manager">
      <div className="flex justify-end"><Button onClick={() => setDlg({ open: true, initial: null })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="user-add-btn"><Plus className="h-4 w-4 mr-2" />Tambah Pengguna</Button></div>
      <div className="xl-wrap">
        <table className="xl">
          <thead><tr><th className="rownum">#</th><th>Username</th><th>Nama</th>{tenants && <th>Perusahaan</th>}<th>Level Akses</th><th>Status</th><th>Login Terakhir</th><th>Aksi</th></tr></thead>
          <tbody>
            {rows.map((u, i) => {
              const role = u.platform_role || u.tenant_role;
              return (
                <tr key={u.id} data-testid={`user-row-${u.username}`}>
                  <td className="rownum">{i + 1}</td>
                  <td className="font-mono">{u.username}</td>
                  <td>{u.name}</td>
                  {tenants && <td>{role === "collector" ? <span className="text-xs text-amber-700" data-testid={`user-collector-tenants-${u.username}`}>{(u.assigned_tenants || []).length ? (u.assigned_tenants || []).map((id) => tenants.find((t) => t.id === id)?.name || "?").join(", ") : "Belum ditugaskan"}</span> : PLATFORM.includes(role) ? <span className="text-xs text-emerald-700">Semua perusahaan</span> : u.tenant_name}</td>}
                  <td><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm ${["owner", "superadmin", "supervisor"].includes(role) ? "bg-emerald-100 text-emerald-800" : role === "collector" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{ROLE_LABEL[role] || u.role_label || "Level Kustom"}</span></td>
                  <td>{u.active ? <span className="text-emerald-700">Aktif</span> : <span className="text-rose-600">Nonaktif</span>}</td>
                  <td className="text-slate-500 text-xs">{fmtDateTime(u.last_login)}</td>
                  <td className="whitespace-nowrap">{!lockedRoles.includes(role) && <Button size="sm" variant="ghost" onClick={() => setDlg({ open: true, initial: u })} data-testid={`user-edit-${u.username}`}><Pencil className="h-4 w-4" /></Button>}{extra && extra(u, load)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <UserDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} initial={dlg.initial} roles={roles} tenants={tenants} onSave={save} />
    </div>
  );
}
